import type {
  HostedAgentLifecycleAction,
  HostedAgentLifecycleImage,
  HostedAgentLifecycleStatus,
} from "@/api/admin-api"
import type { BadgeProps } from "@/components/ui/badge"

// =============================================================================
// Lifecycle helpers (B10/B13). Pure — no React, no i18n.
// =============================================================================

export const HOSTED_AGENT_LIFECYCLE_ACTIONS: HostedAgentLifecycleAction[] = [
  "upgrade",
  "rollback",
  "restart",
  "stop",
  "start",
  "reset",
  "decommission",
]

/** English fallbacks; the locale files carry the translations. */
export const HOSTED_AGENT_LIFECYCLE_STATUS_DEFAULTS: Record<HostedAgentLifecycleStatus, string> = {
  pending: "Pending",
  claimed: "Claimed",
  running: "Running",
  succeeded: "Succeeded",
  failed: "Failed",
  rolled_back: "Rolled back",
  cancelled: "Cancelled",
}

export const HOSTED_AGENT_LIFECYCLE_ACTION_DEFAULTS: Record<HostedAgentLifecycleAction, string> = {
  upgrade: "Upgrade",
  rollback: "Rollback",
  restart: "Restart",
  stop: "Stop",
  start: "Start",
  reset: "Reset",
  decommission: "Remove running environment",
}

export function hostedAgentLifecycleStatusVariant(
  status: HostedAgentLifecycleStatus
): BadgeProps["variant"] {
  switch (status) {
    case "succeeded":
      return "success"
    case "failed":
      return "destructive"
    case "rolled_back":
    case "claimed":
    case "running":
      return "warning"
    case "cancelled":
      return "disabled"
    default:
      return "secondary"
  }
}

// -----------------------------------------------------------------------------
// Image reference validation for an upgrade: digest only.
// `sha256:<64 hex>` or `helloai-hermes@sha256:<64 hex>`. A tag is not an
// upgrade path (D5).
// -----------------------------------------------------------------------------

const DIGEST_RE = /^sha256:([0-9a-f]{64})$/i
const REPO_DIGEST_RE = /^helloai-hermes@sha256:([0-9a-f]{64})$/i

export type HostedAgentImageRefError = "required" | "invalid"

export type HostedAgentImageRefParse =
  | { image: HostedAgentLifecycleImage; kind: "digest"; error: null }
  | { image: null; kind: null; error: HostedAgentImageRefError }

/**
 * Parse the admin's upgrade image into the manifest shape the worker expects.
 * Only an approved digest is an upgrade. A tag is rejected.
 */
export function parseHostedAgentImageRef(raw: string): HostedAgentImageRefParse {
  const value = raw.trim()
  if (!value) return { image: null, kind: null, error: "required" }
  const digest = DIGEST_RE.exec(value) ?? REPO_DIGEST_RE.exec(value)
  if (digest) {
    return {
      image: { imageDigest: `sha256:${digest[1].toLowerCase()}` },
      kind: "digest",
      error: null,
    }
  }
  return { image: null, kind: null, error: "invalid" }
}

/** Short human label for a request's image: "sha256:abcd…" or the tag. */
export function describeHostedAgentLifecycleImage(
  image: HostedAgentLifecycleImage | null
): string | null {
  if (!image) return null
  // The backend serialises both keys (the unused one null) — test values, not keys.
  if (image.imageDigest) return `${image.imageDigest.slice(0, 19)}…`
  return image.imageTag ?? null
}
